/**
 * Registry — descriptor → concrete variant subclass dispatch.
 *
 * `parseRow` walks a Descriptor and instantiates the appropriate
 * typed subclass. Backends / the Sheet factory use this to produce
 * the right subclass instance for each row in a snapshot.
 */

import type {
  AliasDescriptor,
  Descriptor,
  InertDescriptor,
  LayoutDescriptor,
  RendererDescriptor,
  SourceDescriptor,
} from "../descriptor.ts";
import type { RowLocalId } from "../../ids/row-local-id.ts";
import type { ModelSheet } from "../../sheet/rows-sheet.ts";
import type { ModelRow } from "../model-row.ts";

import { AliasRow } from "./alias-row.ts";
import { CalendarAliasRow } from "./calendar-alias-row.ts";
import { CanvasRow } from "./canvas-row.ts";
import { ColumnRow } from "./column-row.ts";
import { DataSource } from "./data-source.ts";
import { FormulaDataSource } from "./formula-data-source.ts";
import { CalendarBodyRow } from "./calendar-body-row.ts";
import { GridRow } from "./grid-row.ts";
import { InertRow } from "./inert-row.ts";
import { instantiateRenderer } from "./hydrate-default-renderer.ts";
import { RendererRow } from "./renderer-row.ts";
import { TimelineColumnRow } from "./timeline-column-row.ts";
import { TimelineRowRow } from "./timeline-row-row.ts";

/**
 * Pick the right subclass for a Descriptor + instantiate it.
 *
 * `rawType` is the wire-format `RawRow.type` string — threaded
 * through to dispatch formula rows to `FormulaDataSource`. Used only
 * for source rows; other roles dispatch on descriptor alone.
 */
export function parseRow(
  id: RowLocalId,
  descriptor: Descriptor,
  sheet: ModelSheet,
  rawType: string,
): ModelRow {
  switch (descriptor.role) {
    case "source":
      return parseSource(id, descriptor, sheet, rawType);
    case "layout":
      return parseLayout(id, descriptor, sheet);
    case "renderer":
      return parseRenderer(id, descriptor, sheet);
    case "inert":
      return new InertRow(id, descriptor as InertDescriptor, sheet);
    case "alias":
      return parseAlias(id, descriptor as AliasDescriptor, sheet);
  }
}

function parseAlias(
  id: RowLocalId,
  d: AliasDescriptor,
  sheet: ModelSheet,
): ModelRow {
  if (d.template === "alias.calendar") return new CalendarAliasRow(id, d, sheet);
  return new AliasRow(id, d, sheet);
}

function parseSource(
  id: RowLocalId,
  d: SourceDescriptor,
  sheet: ModelSheet,
  rawType: string,
): ModelRow {
  // A formula's underlying descriptor is indistinguishable from a
  // user-backed row of the same shape (formulas can emit any dtype),
  // so the wire `rawType` string is the only signal that selects
  // `FormulaDataSource`. Everything else — text / number / boolean
  // / obj across every cardinality — collapses into a single
  // `DataSource`; typed access for scalar dtypes lives on the
  // auto-hydrated `column.*` renderer child (see
  // `hydrate-default-renderer.ts`), and obj dtypes go through
  // facades (`row.as(FACADE_KEY)`).
  if (rawType === "formula") return new FormulaDataSource(id, d, sheet);
  // Computed-source dispatch: wire types registered in
  // the sheet's `registries.computedSources` (calendar-system, …) resolve to a
  // `ComputedDataSource` subclass whose cells are engine-managed
  // (user writes reject). See `./computed-source-registry.ts`.
  const computed = sheet.registries.computedSources.get(rawType);
  if (computed != null) return computed(id, d, sheet);
  return new DataSource(id, d, sheet);
}

function parseLayout(
  id: RowLocalId,
  d: LayoutDescriptor,
  sheet: ModelSheet,
): ModelRow {
  switch (d.layout) {
    case "column":
      return new ColumnRow(id, d, sheet);
    case "canvas":
      return new CanvasRow(id, d, sheet);
    case "timeline[row]":
      return new TimelineRowRow(id, d, sheet);
    case "timeline[column]":
      return new TimelineColumnRow(id, d, sheet);
    case "grid":
      return new GridRow(id, d, sheet);
    case "calendar-body":
      return new CalendarBodyRow(id, d, sheet);
  }
}

function parseRenderer(
  id: RowLocalId,
  d: RendererDescriptor,
  sheet: ModelSheet,
): ModelRow {
  // `instantiateRenderer` dispatches every renderer id in the Part 5
  // catalog (column / canvas / timeline / grid). Auto-hydrated
  // and explicitly-declared renderers both go through here; on re-
  // hydrate from serialize we need the concrete class back, not the
  // generic fallback.
  const concrete = instantiateRenderer(d.rendererId, id, d, sheet);
  if (concrete != null) return concrete;
  // Generic fallback for renderer ids outside the catalog. Consumers
  // read via `.attrs()` / `.descriptor.rendererId`.
  return new RendererRow(id, d, sheet);
}
